import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  DealInvite,
  DealRole,
  DealRolePermissions,
  useUpdateDealInvitePermissionsMutation,
} from "@/src/graphql/generated";
import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import { H4 } from "@/src/components/Heading";
import {
  DealRoleOption,
  DealRolesDropdown,
} from "@/src/components/permissions/DealRolesDropdown";
import { permissionsToInput } from "@/src/components/permissions/dealPermissionConfig";
import { Button } from "@/src/components/tailwind/Button";
import { toasts } from "@/src/components/toasts/toasts";
import useGqlClient from "@/src/hooks/useGqlClient";

export type EditableDealInvite = Pick<DealInvite, "id" | "email"> & {
  dealRole: Pick<DealRole, "id" | "type" | "name"> & {
    permissionLevels: DealRolePermissions;
  };
};

export function EditDealInvitePermissionsButton(props: {
  invite: EditableDealInvite;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button className={props.className} onClick={() => setOpen(true)}>
        Edit
        <span className="sr-only"> role for {props.invite.email}</span>
      </button>
      <EditDealInvitePermissions
        invite={props.invite}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

export function EditDealInvitePermissions(props: {
  invite: EditableDealInvite;
  open: boolean;
  onClose: () => void;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const { dealId } = useParams<{ dealId: string }>();
  const updatePermissions = useUpdateDealInvitePermissionsMutation(client);
  const [selectedRole, setSelectedRole] = useState<DealRoleOption | null>(null);

  useEffect(() => {
    if (props.open) {
      setSelectedRole(null);
    }
  }, [props.invite, props.open]);

  async function save() {
    if (!selectedRole) {
      return;
    }

    try {
      await updatePermissions.mutateAsync({
        input: {
          dealInviteId: props.invite.id,
          permissions: permissionsToInput(selectedRole.permissionLevels),
        },
      });

      queryClient.invalidateQueries({ queryKey: ["Access"] });
      queryClient.invalidateQueries({ queryKey: ["GuestAccessTab"] });
      queryClient.invalidateQueries({ queryKey: ["DealGroup"] });
      queryClient.invalidateQueries({ queryKey: ["DealInvite"] });
      queryClient.invalidateQueries({ queryKey: ["Deal"] });

      toasts.success("Invite role updated");
      props.onClose();
    } catch {
      toasts.error("Failed to update invite role");
    }
  }

  return (
    <AnimatedModal open={props.open} onClose={props.onClose} size="lg">
      <div className="flex items-center justify-between">
        <div>
          <H4 margin="0">Edit invite role</H4>
          <p className="text-sm text-concrete-600">{props.invite.email}</p>
        </div>
        <CloseIcon onClose={props.onClose} />
      </div>

      <div className="mt-4">
        <DealRolesDropdown
          dealId={dealId ?? ""}
          roleType={props.invite.dealRole.type}
          value={selectedRole?.id ?? props.invite.dealRole.id}
          onChange={setSelectedRole}
          label="Role"
        />
      </div>

      <div className="mt-5 flex justify-end gap-x-2 border-t border-concrete-100 pt-4">
        <Button text="Cancel" variant="neutral" onClick={props.onClose} />
        <Button
          text="Save"
          variant="positive"
          isLoading={updatePermissions.isPending}
          loadingText="Saving..."
          isDisabled={!selectedRole}
          onClick={save}
        />
      </div>
    </AnimatedModal>
  );
}
